import { useEffect, useRef } from "react";
import { waLink, WHATSAPP_INTENTS } from "../config";

const badges = [
  { icon: "ri-calendar-check-line", label: "Günlük Hazırlanır" },
  { icon: "ri-fire-line", label: "Sıcak Teslim" },
  { icon: "ri-list-check-3", label: "Menünü Sen Seçersin" },
];

export default function Hero() {
  const imgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => {
      if (imgRef.current && window.scrollY < window.innerHeight) {
        const offset = window.scrollY * 0.12;
        imgRef.current.style.transform = `translateY(${offset}px)`;
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section id="top" className="relative overflow-hidden bg-background-100">
      {/* Subtle background glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-primary-100/40 blur-3xl"></div>
        <div className="absolute bottom-0 -left-24 w-96 h-96 rounded-full bg-accent-100/40 blur-3xl"></div>
      </div>

      <div className="relative mx-auto max-w-7xl px-4 md:px-6 pt-24 md:pt-32 pb-12 md:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          {/* Left: text */}
          <div className="w-full">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-xs font-bold tracking-wider whitespace-nowrap">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-500"></span>
              EV YEMEĞİ • GÜNLÜK HAZIRLIK • SICAK TESLİM
            </div>

            <h1 className="mt-6 font-heading text-4xl md:text-6xl lg:text-[64px] leading-[1.05] text-foreground-950 font-bold animate-fade-up">
              BUGÜN NE
              <br />
              YİYECEĞİNE
              <br />
              <span className="text-primary-600">SEN KARAR VER.</span>
            </h1>

            <p className="mt-6 text-base md:text-lg text-foreground-600 max-w-md leading-relaxed animate-fade-up delay-100">
              Sevdiğin ev yemeklerini seç. Biz günlük hazırlayalım, planlanan
              teslimat saatinde kapına getirelim.
            </p>

            {/* Price card */}
            <div className="mt-7 inline-flex items-center gap-5 bg-background-50 border border-background-200 rounded-lg px-6 py-4 animate-fade-up delay-200">
              <div className="leading-none">
                <span className="font-heading text-4xl md:text-5xl font-bold text-foreground-950">
                  330
                </span>
                <span className="font-heading text-2xl text-foreground-700">₺</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm font-bold text-foreground-800">Günlük Öğün</span>
                <div className="flex flex-wrap gap-1.5">
                  {badges.map((b) => (
                    <span
                      key={b.label}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent-100 text-accent-900 text-[11px] font-semibold whitespace-nowrap"
                    >
                      <i className={`${b.icon} text-xs`}></i>
                      {b.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* CTAs */}
            <div className="mt-7 flex flex-col sm:flex-row gap-3 animate-fade-up delay-300">
              <a
                href="#menu"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 text-base font-bold hover:bg-primary-600 transition-all hover:-translate-y-0.5 whitespace-nowrap cursor-pointer"
              >
                BUGÜNKÜ YEMEKLERİ GÖR
                <i className="ri-arrow-right-line"></i>
              </a>
              <a
                href={waLink(WHATSAPP_INTENTS.hero)}
                target="_blank"
                rel="nofollow noopener"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-md border-2 border-accent-500 text-accent-900 text-base font-bold hover:bg-accent-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                WHATSAPP'TAN BİLGİ AL
              </a>
            </div>

            <p className="mt-5 text-xs text-foreground-500 animate-fade-up delay-400">
              Meliha Sultan • Ev yemeği rahatlığı, seçim özgürlüğü ve planlı teslimat
            </p>
          </div>

          {/* Right: food image */}
          <div className="relative w-full animate-fade-up delay-200">
            <div className="relative rounded-2xl overflow-hidden aspect-[4/5] bg-background-200">
              <div ref={imgRef} className="absolute inset-0 will-change-transform">
                <img
                  src="https://readdy.ai/api/search-image?query=Overhead%20Turkish%20home%20cooking%20feast%20table%20with%20main%20dish%20rice%20pilaf%20soup%20salad%20yogurt%20and%20fresh%20bread%20on%20warm%20rustic%20wooden%20table%2C%20steam%20rising%2C%20soft%20golden%20natural%20light%2C%20home%20kitchen%20atmosphere%2C%20editorial%20food%20photography%2C%20appetizing%20authentic%20Turkish%20cuisine&width=900&height=1100&seq=hero-sofra&orientation=portrait"
                  alt="Meliha Sultan ev yemekleri sofra düzeni"
                  title="Meliha Sultan Ev Yemekleri - ev yemeği sofra"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Floating badge */}
            <div className="absolute -bottom-4 -left-4 md:-left-6 bg-background-50 border border-background-200 rounded-lg px-4 py-3 animate-float">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center">
                  <i className="ri-heart-3-line"></i>
                </div>
                <div className="leading-tight">
                  <span className="block text-sm font-bold text-foreground-900 whitespace-nowrap">
                    30 Yıllık Tecrübe
                  </span>
                  <span className="block text-xs text-foreground-500">Anne eli değmiş lezzet</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}